import { all, call, put, take, takeLatest, select, cancelled, fork, cancel } from 'redux-saga/effects';
import { eventChannel, type EventChannel } from 'redux-saga';
import ENDPOINTS from '../../api/endPoints';
import { buildCustomizationPayload } from '../../utils/buildCustomizationPayload';
import type { CustomizationParams, CustomizationFiles } from '../../types/customization.types';
import type { RootState } from '../store';
import {
  fetchSwatchesRequest,
  fetchSwatchesSuccess,
  fetchSwatchesFailure,
  customizeRequest,
  customizeStreamResult,
  customizeStreamError,
  customizeStreamProgress,
  customizeFailure,
  customizeCancel,
  regenerateImageRequest,
  regenerateImageDeleteSuccess,
  regenerateImageComplete,
  regenerateImageFailure,
} from '../slices/customizationSlice';

async function fetchJson(url: string): Promise<string[]> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed: ${url} (${res.statusText})`);
  return res.json();
}

function* fetchSwatchesSaga(): Generator<any, void, any> {
  try {
    const [
      frameFabrics,
      seatFabrics,
      backCushionFabrics,
      armrestFabrics,
      woodFinishes,
      nailheadSizes,
      // Demo-2 swatches
      ralphTwoFabric,
      ralphSofaChairFinishes,
      ralphChairSeatFabrics,
      ralphChairBackFabrics,
      ralphChairArmrestFabrics,
    ] = yield all([
      call(fetchJson, ENDPOINTS.frameFabrics),
      call(fetchJson, ENDPOINTS.seatFabrics),
      call(fetchJson, ENDPOINTS.backCushionFabrics),
      call(fetchJson, ENDPOINTS.armrestFabrics),
      call(fetchJson, ENDPOINTS.woodFinishes),
      call(fetchJson, ENDPOINTS.nailheadSizes),
      // Demo-2 swatches
      call(fetchJson, ENDPOINTS.ralphTwoFabricSofa),
      call(fetchJson, ENDPOINTS.ralphSofaChairWoodFinishes),
      call(fetchJson, ENDPOINTS.ralphChairSeatFabrics),
      call(fetchJson, ENDPOINTS.ralphChairBackCushionFabrics),
      call(fetchJson, ENDPOINTS.ralphChairArmrestFabrics),
    ]);

    yield put(fetchSwatchesSuccess({
      frameFabrics,
      seatFabrics,
      backCushionFabrics,
      armrestFabrics,
      woodFinishes,
      nailheadSizes,
      ralphTwoFabric,
      ralphSofaChairFinishes,
      ralphChairSeatFabrics,
      ralphChairBackFabrics,
      ralphChairArmrestFabrics,
    }));
  } catch (error: any) {
    yield put(fetchSwatchesFailure(error.message || 'Failed to fetch swatches'));
  }
}

// SSE event channel for streaming customization results
function createSSEChannel(url: string, formData: FormData): EventChannel<any> {
  return eventChannel((emitter) => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(url, { method: 'POST', body: formData, signal: controller.signal });
        if (!response.ok) {
          emitter({ type: 'error', message: `Request failed: ${response.status} ${response.statusText}` });
          emitter(new Error('END'));
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          emitter({ type: 'error', message: 'No readable stream available' });
          emitter(new Error('END'));
          return;
        }

        const isSSE = (response.headers.get('content-type') || '').includes('text/event-stream');
        const decoder = new TextDecoder();
        let buffer = '';

        const processJson = (jsonStr: string) => {
          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.done === true) {
              emitter({ type: 'progress', data: parsed });
            } else if (parsed.error && !parsed.image_url) {
              emitter({ type: 'item_error', data: parsed });
            } else if (parsed.image_url) {
              emitter({ type: 'result', data: parsed });
            }
          } catch { /* skip unparseable */ }
        };

        const parseSSEBlocks = (text: string): { remainder: string } => {
          const blocks = text.split('\n\n');
          const remainder = blocks.pop() || '';
          for (const block of blocks) {
            if (!block.trim()) continue;
            const dataLines: string[] = [];
            for (const line of block.split('\n')) {
              if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
              else if (!line.startsWith('event:') && !line.startsWith(':') && line.trim()) dataLines.push(line.trim());
            }
            if (dataLines.length > 0) processJson(dataLines.join('\n'));
          }
          return { remainder };
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          if (isSSE) {
            const result = parseSSEBlocks(buffer);
            buffer = result.remainder;
          } else {
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (line.trim()) processJson(line.trim());
            }
          }
        }

        if (buffer.trim()) {
          if (isSSE) parseSSEBlocks(buffer + '\n\n');
          else processJson(buffer.trim());
        }

        emitter({ type: 'done' });
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          emitter({ type: 'error', message: err.message || 'Stream failed' });
        }
        emitter({ type: 'done' });
      }
    })();

    return () => controller.abort();
  });
}

function* customizeFurnitureSaga(action: ReturnType<typeof customizeRequest>): Generator<any, void, any> {
  const { params, files } = action.payload;
  const { url, formData } = buildCustomizationPayload(params, files);
  const channel: EventChannel<any> = yield call(createSSEChannel, url, formData);

  try {
    while (true) {
      const event = yield take(channel);

      if (event.type === 'result') {
        yield put(customizeStreamResult({
          index: event.data.index,
          filename: event.data.filename,
          image_url: event.data.image_url,
          source_image_url: event.data.source_image_url,
          customizations_applied: event.data.customizations_applied || [],
          cached: event.data.cached ?? false,
          cache_hash: event.data.cache_hash || undefined,
        }));
      } else if (event.type === 'item_error') {
        yield put(customizeStreamError({
          index: event.data.index,
          filename: event.data.filename,
          error: event.data.error,
        }));
      } else if (event.type === 'progress') {
        yield put(customizeStreamProgress({
          done: true,
          total: event.data.total,
          succeeded: event.data.succeeded,
          failed: event.data.failed,
        }));
      } else if (event.type === 'error') {
        yield put(customizeFailure(event.message));
        break;
      } else if (event.type === 'done') {
        break;
      }
    }
  } catch (error: any) {
    yield put(customizeFailure(error.message || 'Customization failed'));
  } finally {
    if (yield cancelled()) {
      if (channel) channel.close();
    }
  }
}

function* watchCustomizeRequest(): Generator<any, void, any> {
  let task: any = null;
  while (true) {
    const action = yield take([customizeRequest.type, customizeCancel.type]);

    if (task) yield cancel(task);

    if (action.type === customizeRequest.type) {
      task = yield fork(customizeFurnitureSaga, action);
    }
  }
}

// Delete cache API call
async function deleteCacheApi(cacheHash: string): Promise<void> {
  const url = `${ENDPOINTS.deleteCache}?cache_hash=${cacheHash}`;
  const res = await fetch(url, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete cache failed: ${res.status} ${res.statusText}`);
}

function* regenerateImageSaga(action: ReturnType<typeof regenerateImageRequest>): Generator<any, void, any> {
  const { cacheHash } = action.payload;

  try {
    // Step 1: Delete cache for the specific image
    yield call(deleteCacheApi, cacheHash);
    yield put(regenerateImageDeleteSuccess());

    // Step 2: Get the last customization payload and re-dispatch
    const lastPayload: { params: CustomizationParams; files: CustomizationFiles } | null =
      yield select((state: RootState) => state.customization.lastCustomizationPayload);

    if (!lastPayload) {
      yield put(regenerateImageFailure('No previous customization to regenerate'));
      return;
    }

    // Re-dispatch the customization request with the same params/files
    yield put(customizeRequest(lastPayload));
    yield put(regenerateImageComplete());
  } catch (error: any) {
    yield put(regenerateImageFailure(error.message || 'Regeneration failed'));
  }
}

export function* customizationSaga() {
  yield all([
    takeLatest(fetchSwatchesRequest.type, fetchSwatchesSaga),
    takeLatest(regenerateImageRequest.type, regenerateImageSaga),
    call(watchCustomizeRequest),
  ]);
}
